import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import DealsLayout from './components/deals/DealsLayout';
import DealsHome from './components/deals/DealsHome';
import DealsList from './components/deals/DealsList';
import DealDetail from './components/deals/DealDetail';
import SubscribePage from './components/deals/SubscribePage';
import ReferralsPage from './components/deals/ReferralsPage';
import AdminDashboard from './components/admin/AdminDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send cookies (session_token) with every API call so Google OAuth works
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('Auth provider initializing...');
    console.log('URL:', window.location.href);
    console.log('Hash:', window.location.hash);
    
    // Check for Google OAuth session ID in URL first
    const urlFragment = window.location.hash;
    if (urlFragment.includes('session_id=')) {
      const sessionId = urlFragment.split('session_id=')[1].split('&')[0];
      console.log('Found session ID in URL:', sessionId);
      processGoogleSession(sessionId);
      return;
    }

    // Check existing session (cookie-based) or JWT token
    const token = localStorage.getItem('token');
    if (token) {
      console.log('Found JWT token, using token auth');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      console.log('No token found, checking for cookie auth');
      // Try cookie-based auth (Google OAuth)
      fetchUser();
    }
  }, []);

  const processGoogleSession = async (sessionId) => {
    try {
      setLoading(true);
      console.log('Processing Google session ID:', sessionId);
      
      // Clear URL fragment
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      
      const response = await axios.post(`${API}/auth/google/session?session_id=${sessionId}`);
      console.log('Google auth success:', response.data);
      
      // Check if there's a fallback JWT token in the response headers
      const jwtToken = response.headers['x-auth-token'];
      if (jwtToken) {
        console.log('Using fallback JWT token for authentication');
        localStorage.setItem('token', jwtToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
      }
      
      setUser(response.data.user);
    } catch (error) {
      console.error('Google auth error:', error);
      alert('Google authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      console.log('User fetched successfully:', response.data);
      setUser(response.data);
    } catch (error) {
      console.log('Failed to fetch user:', error.response?.status);
      if (error.response?.status === 401) {
        // Clear any invalid tokens
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      await fetchUser();
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const register = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      await fetchUser();
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const loginWithGoogle = () => {
    const redirectUrl = `${window.location.origin}`;
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loginWithGoogle, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Growth-Focused Landing Page Component
const GrowthLandingPage = () => {
  const { login, register, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    // Check for referral code in URL
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
    }
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await register(email, password);
      
      // Apply referral code if present
      if (referralCode) {
        try {
          await axios.post(`${API}/referral/apply?referral_code=${referralCode}`);
        } catch (refError) {
          console.warn('Failed to apply referral code:', refError);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-caribbean-hero">
      {/* Header */}
      <header className="bg-caribbean-gradient shadow-xl border-b border-caribbean-gold border-opacity-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-caribbean-gold-gradient rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-caribbean-black text-2xl font-bold">💰</span>
              </div>
              <h1 className="text-3xl font-bold text-caribbean-gold">DealPing</h1>
            </div>
            <a href="/" className="text-caribbean-gold hover:text-white font-semibold">Back to Home</a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          {/* Primary Headline */}
          <h1 className="text-7xl font-black text-white mb-6 leading-tight">
            Stop <span className="text-caribbean-gold">Overpaying</span> Online.
          </h1>
          <h2 className="text-4xl font-bold text-caribbean-gold mb-8">
            DealPing Watches Prices So You Don't.
          </h2>
          
          {/* Supporting Subheadline */}
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            Get instant alerts when prices drop on Amazon, Walmart, Target & more — 
            <span className="text-caribbean-gold font-bold"> before everyone else finds out.</span>
          </p>

          {/* Three Benefit-Driven Bullets */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="w-20 h-20 bg-caribbean-gold-gradient rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                <span className="text-4xl">⚡</span>
              </div>
              <h3 className="text-2xl font-bold text-caribbean-gold mb-3">Never Miss a Drop</h3>
              <p className="text-xl text-gray-300">Get notified the second prices hit rock bottom</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-caribbean-gold-gradient rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                <span className="text-4xl">💎</span>
              </div>
              <h3 className="text-2xl font-bold text-caribbean-gold mb-3">Buy at Lowest Price</h3>
              <p className="text-xl text-gray-300">Always purchase at the absolute best deal</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-caribbean-gold-gradient rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                <span className="text-4xl">🎯</span>
              </div>
              <h3 className="text-2xl font-bold text-caribbean-gold mb-3">Let DealPing Watch</h3>
              <p className="text-xl text-gray-300">Set it once, we'll handle the rest automatically</p>
            </div>
          </div>
        </div>

        {/* Visual Preview Mock */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-caribbean-gradient rounded-3xl p-8 shadow-2xl border-2 border-caribbean-gold border-opacity-30">
            <h3 className="text-3xl font-bold text-caribbean-gold mb-6 text-center">See DealPing In Action</h3>
            <div className="bg-black bg-opacity-50 rounded-2xl p-6">
              <div className="space-y-4">
                <div className="bg-green-900 bg-opacity-40 border border-green-400 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">🚨</span>
                    <div>
                      <div className="text-caribbean-gold font-bold">Price Drop Detected!</div>
                      <div className="text-white">Sony Headphones dropped from $299 → $179 (40% off)</div>
                      <div className="text-green-400 text-sm">Buy now before price rebounds!</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-orange-900 bg-opacity-40 border border-orange-400 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">⏳</span>
                    <div>
                      <div className="text-caribbean-gold font-bold">Don't Buy This Yet!</div>
                      <div className="text-white">iPhone Case - Price predicted to drop 25% in 3 days</div>
                      <div className="text-orange-400 text-sm">We'll alert you when it's time to buy</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Proof */}
        <div className="text-center mb-16">
          <div className="bg-caribbean-black bg-opacity-60 rounded-2xl p-8 border border-caribbean-gold border-opacity-20">
            <h3 className="text-2xl font-bold text-caribbean-gold mb-4">Join 1,000+ Smart Shoppers</h3>
            <p className="text-xl text-gray-300 mb-6">Beta testers are already saving an average of $247/month</p>
            <div className="flex justify-center space-x-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-caribbean-gold">$2.1M+</div>
                <div className="text-gray-400">Total Saved</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-caribbean-gold">15,000+</div>
                <div className="text-gray-400">Price Drops Caught</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-caribbean-gold">98%</div>
                <div className="text-gray-400">Success Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Capture / Account Creation */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-caribbean-gradient rounded-3xl p-12 shadow-2xl border-2 border-caribbean-gold">
            <h3 className="text-4xl font-bold text-caribbean-gold mb-8 text-center">Start Saving Today</h3>
            
            {referralCode && (
              <div className="bg-green-900 bg-opacity-30 border border-green-400 rounded-lg p-4 mb-6 text-center">
                <span className="text-green-400 font-bold">🎉 You've been invited! Special bonus rewards await.</span>
              </div>
            )}

            <form onSubmit={handleSignup} className="space-y-6">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-6 py-4 bg-white border-2 border-caribbean-gold rounded-xl focus:outline-none focus:ring-2 focus:ring-caribbean-gold text-caribbean-black font-semibold text-lg"
                  placeholder="Enter your email to get free alerts"
                  required
                />
              </div>

              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-6 py-4 bg-white border-2 border-caribbean-gold rounded-xl focus:outline-none focus:ring-2 focus:ring-caribbean-gold text-caribbean-black font-semibold text-lg"
                  placeholder="Create a secure password"
                  required
                  minLength={6}
                />
              </div>

              {error && (
                <div className="text-red-400 text-center font-semibold bg-red-900 bg-opacity-30 p-4 rounded-lg">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-caribbean-gold py-5 rounded-2xl text-2xl font-black disabled:opacity-50 transform hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                {loading ? '💰 Setting Up Your Alerts...' : '💰 Get Free Alerts'}
              </button>
            </form>

            <div className="mt-8 text-center">
              <button
                onClick={loginWithGoogle}
                disabled={loading}
                className="w-full bg-white border-2 border-caribbean-gold text-caribbean-black py-4 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center space-x-3 font-bold text-lg shadow-lg"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>🚀 Get Free Alerts with Google</span>
              </button>
            </div>

            <div className="mt-8 text-center">
              <a href="#how-it-works" className="text-caribbean-gold hover:text-white font-bold text-xl underline">
                See How It Works →
              </a>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div id="how-it-works" className="mt-20">
          <h2 className="text-5xl font-bold text-center text-caribbean-gold mb-12">How DealPing Beats The System</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-caribbean-gold rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-caribbean-black">1</div>
              <h3 className="text-2xl font-bold text-white mb-3">Paste Any Product URL</h3>
              <p className="text-lg text-gray-300">Amazon, Walmart, Target, or any major retailer</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-caribbean-gold rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-caribbean-black">2</div>
              <h3 className="text-2xl font-bold text-white mb-3">Set Your Target Price</h3>
              <p className="text-lg text-gray-300">We monitor prices 24/7 with military precision</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-caribbean-gold rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-caribbean-black">3</div>
              <h3 className="text-2xl font-bold text-white mb-3">Get Instant Alerts</h3>
              <p className="text-lg text-gray-300">Buy before the crowd catches on</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Landing Page Component
const LandingPage = () => {
  const { login, register, loginWithGoogle } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);

  // Check for promo code in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlPromoCode = urlParams.get('promo');
    if (urlPromoCode) {
      setPromoCode(urlPromoCode.toUpperCase());
      validatePromoCode(urlPromoCode.toUpperCase());
      setShowAuthModal(true);
      setIsLoginMode(false); // Default to signup for promo users
    }
  }, []);

  const validatePromoCode = async (code) => {
    if (!code) {
      setPromoValidation(null);
      return;
    }

    try {
      const response = await axios.get(`${API}/promo/validate/${code}`);
      setPromoValidation(response.data);
    } catch (error) {
      setPromoValidation({ valid: false, message: 'Invalid promotional code' });
    }
  };

  const handlePromoCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setPromoCode(code);
    if (code.length >= 3) {
      validatePromoCode(code);
    } else {
      setPromoValidation(null);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLoginMode) {
        await login(email, password);
      } else {
        await register(email, password);
        
        // If there's a valid promo code, redeem it after registration
        if (promoCode && promoValidation?.valid) {
          try {
            await axios.post(`${API}/promo/redeem?promo_code=${promoCode}`);
          } catch (promoError) {
            console.warn('Failed to redeem promo code:', promoError);
          }
        }
      }
      setShowAuthModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-caribbean-hero">
      {/* Header */}
      <header className="bg-caribbean-gradient shadow-xl border-b border-caribbean-gold border-opacity-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-caribbean-gold-gradient rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-caribbean-black text-xl font-bold">🏝️</span>
              </div>
              <h1 className="text-3xl font-bold text-caribbean-gold">DealPing</h1>
              <span className="text-xs text-caribbean-gold-dark font-semibold tracking-wider">CARIBBEAN</span>
            </div>
            <button
              onClick={() => setShowAuthModal(true)}
              className="btn-caribbean-gold px-8 py-3 rounded-xl font-bold text-lg shadow-lg"
            >
              Get Started 🌴
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {promoCode && promoValidation?.valid && (
          <div className="bg-caribbean-gold-gradient text-caribbean-black p-8 rounded-2xl mb-12 text-center shadow-2xl border-2 border-caribbean-gold">
            <div className="text-3xl font-bold mb-3">🏆 Caribbean Special Activated!</div>
            <div className="text-xl font-semibold">{promoValidation.description}</div>
            <div className="text-sm mt-3 font-bold tracking-wider">Promo Code: {promoCode}</div>
          </div>
        )}
        
        <div className="text-center mb-20">
          <h2 className="text-7xl font-black text-white mb-8 leading-tight">
            Stop <span className="text-caribbean-gold">Overpaying</span> Online.
          </h2>
          <h3 className="text-3xl font-bold text-caribbean-gold mb-8">
            DealPing Watches Prices So You Don't.
          </h3>
          <div className="text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            Get instant alerts when prices drop on Amazon, Walmart, Target & more — 
            <br /><span className="text-caribbean-gold font-semibold">before everyone else finds out! 🏖️</span>
          </div>
          <button
            onClick={() => setShowAuthModal(true)}
            className="btn-caribbean-gold px-16 py-6 rounded-2xl text-3xl font-black shadow-2xl transform hover:scale-105 transition-all duration-300"
          >
            {promoCode && promoValidation?.valid ? '🏆 Get Free Alerts + Bonus' : '💰 Get Free Alerts'}
          </button>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-10 mb-20">
          <div className="card-caribbean p-10 rounded-2xl shadow-2xl">
            <div className="w-16 h-16 bg-caribbean-gold-gradient rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <span className="text-3xl">📊</span>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-caribbean-gold">Price Monitoring</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              Automatically track prices across Amazon, Walmart, and Target with Caribbean precision. Get historical price charts and trends that flow like island waves.
            </p>
          </div>
          
          <div className="card-caribbean p-10 rounded-2xl shadow-2xl">
            <div className="w-16 h-16 bg-caribbean-gold-gradient rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <span className="text-3xl">⚡</span>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-caribbean-gold">Lightning Alerts</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              Get instant email notifications faster than Caribbean lightning when prices drop below your target or items return to stock.
            </p>
          </div>
          
          <div className="card-caribbean p-10 rounded-2xl shadow-2xl">
            <div className="w-16 h-16 bg-caribbean-gold-gradient rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <span className="text-3xl">🎯</span>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-caribbean-gold">Smart Island Tracking</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              Simply paste any product URL and let our Caribbean-powered AI handle the rest. Set custom price targets with island-time reliability.
            </p>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-caribbean-gradient rounded-3xl shadow-2xl p-12 border-2 border-caribbean-gold border-opacity-30">
          <h3 className="text-4xl font-bold text-center mb-12 text-caribbean-gold">Caribbean Pricing Plans 🏖️</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border-2 border-gray-300 rounded-2xl p-8 text-center shadow-lg">
              <h4 className="text-2xl font-bold mb-3 text-caribbean-black">Island Starter</h4>
              <div className="text-4xl font-bold mb-6 text-caribbean-black">$0<span className="text-xl font-normal text-gray-600">/mo</span></div>
              <ul className="text-gray-700 mb-8 space-y-3 text-lg">
                <li>🏝️ Track up to 3 items</li>
                <li>📧 Email alerts</li>
                <li>📈 Price history</li>
              </ul>
              <button 
                onClick={() => setShowAuthModal(true)}
                className="w-full btn-caribbean-black py-3 rounded-xl text-lg font-bold"
              >
                Start Free 🌴
              </button>
            </div>
            
            <div className="bg-caribbean-gold-gradient border-4 border-caribbean-gold rounded-2xl p-8 text-center relative shadow-2xl transform scale-105">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-caribbean-black text-caribbean-gold px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                🏆 ISLAND FAVORITE
              </div>
              <h4 className="text-2xl font-bold mb-3 text-caribbean-black">Caribbean Basic</h4>
              <div className="text-4xl font-bold mb-6 text-caribbean-black">$9.99<span className="text-xl font-normal text-gray-700">/mo</span></div>
              <ul className="text-gray-800 mb-8 space-y-3 text-lg font-semibold">
                <li>🏖️ Track up to 25 items</li>
                <li>⚡ Lightning alerts</li>
                <li>📊 Price history & trends</li>
                <li>🎯 Priority support</li>
              </ul>
              <button 
                onClick={() => setShowAuthModal(true)}
                className="w-full bg-caribbean-black text-caribbean-gold py-3 rounded-xl hover:bg-gray-800 transition-colors text-lg font-bold border-2 border-caribbean-black"
              >
                Start Island Trial 🏝️
              </button>
            </div>
            
            <div className="bg-white border-2 border-gray-300 rounded-2xl p-8 text-center shadow-lg">
              <h4 className="text-2xl font-bold mb-3 text-caribbean-black">Island Pro</h4>
              <div className="text-4xl font-bold mb-6 text-caribbean-black">$19.99<span className="text-xl font-normal text-gray-600">/mo</span></div>
              <ul className="text-gray-700 mb-8 space-y-3 text-lg">
                <li>🌊 Track up to 100 items</li>
                <li>📧 Premium alerts</li>
                <li>📈 Advanced analytics</li>
                <li>🎯 VIP support</li>
                <li>🏆 Caribbean insights</li>
              </ul>
              <button 
                onClick={() => setShowAuthModal(true)}
                className="w-full btn-caribbean-black py-3 rounded-xl text-lg font-bold"
              >
                Go Pro 👑
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-caribbean-gradient rounded-2xl max-w-md w-full p-8 shadow-2xl border-2 border-caribbean-gold border-opacity-30">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-3xl font-bold text-caribbean-gold">
                {isLoginMode ? '🏝️ Welcome Back' : '🌴 Join the Island'}
              </h3>
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  setError('');
                }}
                className="text-caribbean-gold hover:text-white text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAuth} className="space-y-6">
              <div>
                <label className="block text-lg font-semibold text-caribbean-gold mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-caribbean-gold border-opacity-30 rounded-xl focus:outline-none focus:ring-2 focus:ring-caribbean-gold focus:border-caribbean-gold text-caribbean-black font-semibold"
                  required
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-lg font-semibold text-caribbean-gold mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-caribbean-gold border-opacity-30 rounded-xl focus:outline-none focus:ring-2 focus:ring-caribbean-gold focus:border-caribbean-gold text-caribbean-black font-semibold"
                  required
                  minLength={6}
                  placeholder="Enter your password"
                />
              </div>

              {!isLoginMode && (
                <div>
                  <label className="block text-lg font-semibold text-caribbean-gold mb-2">
                    🎫 Promo Code (Optional)
                  </label>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={handlePromoCodeChange}
                    placeholder="Enter your island code"
                    className="w-full px-4 py-3 bg-white border-2 border-caribbean-gold border-opacity-30 rounded-xl focus:outline-none focus:ring-2 focus:ring-caribbean-gold focus:border-caribbean-gold text-caribbean-black font-semibold"
                  />
                  {promoValidation?.valid && (
                    <div className="text-caribbean-gold text-base mt-2 font-semibold bg-green-900 bg-opacity-30 p-3 rounded-lg">
                      🏆 {promoValidation.description}
                    </div>
                  )}
                  {promoValidation?.valid === false && (
                    <div className="text-red-400 text-base mt-2 font-semibold bg-red-900 bg-opacity-30 p-3 rounded-lg">
                      ❌ {promoValidation.message}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              {error && (
                <div className="text-red-400 text-base font-semibold bg-red-900 bg-opacity-30 p-3 rounded-lg">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-caribbean-gold py-4 rounded-xl text-xl font-bold disabled:opacity-50 transform hover:scale-105 transition-all duration-300"
              >
                {loading ? '🌴 Loading...' : (isLoginMode ? '🏝️ Sign In' : (promoValidation?.valid ? '🏆 Sign Up & Claim Offer' : '🌴 Create Account'))}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-caribbean-gold border-opacity-30" />
                </div>
                <div className="relative flex justify-center text-lg">
                  <span className="px-4 bg-caribbean-gradient text-caribbean-gold font-bold">Or</span>
                </div>
              </div>

              <button
                onClick={loginWithGoogle}
                disabled={loading}
                className="w-full mt-6 bg-white border-2 border-caribbean-gold text-caribbean-black py-4 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center space-x-3 font-bold text-lg shadow-lg"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>🏖️ {isLoginMode ? 'Sign in with Google' : 'Join with Google'}</span>
              </button>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsLoginMode(!isLoginMode);
                  setError('');
                }}
                className="text-caribbean-gold hover:text-white text-lg font-semibold transition-colors"
              >
                {isLoginMode ? "🌴 New to the island? Join us" : "🏝️ Already have an account? Welcome back"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemUrl, setNewItemUrl] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [isStockAlert, setIsStockAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scrapedProduct, setScrapedProduct] = useState(null);
  const [plans, setPlans] = useState({});
  const [trialInfo, setTrialInfo] = useState({ is_trial: false });
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  useEffect(() => {
    fetchItems();
    fetchPlans();
    checkPaymentStatus();
    fetchTrialInfo();
  }, []);

  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettingsMenu && !event.target.closest('.relative')) {
        setShowSettingsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsMenu]);

  const fetchTrialInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setTrialInfo(response.data.trial_info || { is_trial: false });
    } catch (error) {
      console.error('Error fetching trial info:', error);
    }
  };

  const checkPaymentStatus = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) return;

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        alert('Payment successful! Your subscription has been upgraded.');
        window.location.href = '/dashboard';
        return;
      } else if (response.data.status === 'expired') {
        alert('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/items`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const scrapeProduct = async () => {
    if (!newItemUrl) return;

    setLoading(true);
    setError('');

    try {
      // Add cache busting parameter to ensure fresh data
      const cacheBreaker = `&_cb=${Date.now()}`;
      const response = await axios.post(`${API}/products/scrape?url=${encodeURIComponent(newItemUrl)}${cacheBreaker}`, {}, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      setScrapedProduct(response.data);
      console.log('Scraped product data:', response.data); // Debug log
    } catch (error) {
      console.error('Scraping error:', error); // Debug log
      setError(error.response?.data?.detail || 'Failed to scrape product');
    } finally {
      setLoading(false);
    }
  };

  const addItem = async () => {
    if (!scrapedProduct) return;

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/items/track`, {
        url: newItemUrl,
        target_price: targetPrice ? parseFloat(targetPrice) : null,
        is_stock_alert: isStockAlert
      });

      setShowAddModal(false);
      setNewItemUrl('');
      setTargetPrice('');
      setIsStockAlert(false);
      setScrapedProduct(null);
      fetchItems();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/items/${itemId}`);
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const upgradeSubscription = async (planId) => {
    try {
      const response = await axios.post(`${API}/payments/checkout?plan_id=${planId}`);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create checkout session');
    }
  };

  const currentPlan = trialInfo.is_trial 
    ? { ...plans[trialInfo.trial_plan], name: `${plans[trialInfo.trial_plan]?.name} (Trial)` }
    : (plans[user?.subscription_plan] || plans.free || { name: 'Free', item_limit: 3 });
  
  const canAddMore = items.length < (currentPlan?.item_limit || 3);

  // Calculate days left in trial
  const trialDaysLeft = trialInfo.is_trial && trialInfo.trial_ends_at 
    ? Math.ceil((new Date(trialInfo.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="min-h-screen bg-caribbean-hero">
      {/* Header */}
      <header className="bg-caribbean-gradient shadow-2xl border-b-2 border-caribbean-gold border-opacity-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-caribbean-gold-gradient rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-caribbean-black text-2xl font-bold">🏝️</span>
              </div>
              <h1 className="text-3xl font-bold text-caribbean-gold">DealPing</h1>
              <span className="text-xs text-caribbean-gold-dark font-bold tracking-wider">CARIBBEAN</span>
            </div>
            <div className="flex items-center space-x-4">
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name || user.email}
                  className="w-8 h-8 rounded-full"
                />
              )}
              <div className="text-sm text-gray-600">
                {user?.name || user?.email} • {currentPlan?.name} Plan
              </div>
              
              {/* Settings Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                  className="text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  ⚙️
                </button>
                
                {showSettingsMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      {!trialInfo.is_trial && currentPlan?.name === 'Free' && (
                        <button
                          onClick={() => {
                            upgradeSubscription('basic');
                            setShowSettingsMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <span className="text-yellow-500 mr-2">⭐</span>
                          Upgrade to Basic
                        </button>
                      )}
                      
                      {!trialInfo.is_trial && currentPlan?.name !== 'Pro' && (
                        <button
                          onClick={() => {
                            upgradeSubscription('pro');
                            setShowSettingsMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <span className="text-purple-500 mr-2">💎</span>
                          Upgrade to Pro
                        </button>
                      )}
                      
                      {trialInfo.is_trial && (
                        <button
                          onClick={() => {
                            upgradeSubscription(trialInfo.trial_plan);
                            setShowSettingsMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <span className="text-green-500 mr-2">🚀</span>
                          Keep {plans[trialInfo.trial_plan]?.name}
                        </button>
                      )}
                      
                      <div className="border-t border-gray-100"></div>
                      
                      <button
                        onClick={() => {
                          logout();
                          setShowSettingsMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <span className="text-red-500 mr-2">🚪</span>
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trial Status Banner */}
        {trialInfo.is_trial && (
          <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">🎉 You're on a FREE trial of {plans[trialInfo.trial_plan]?.name}!</div>
                <div className="text-sm opacity-90">
                  {trialDaysLeft > 0 ? `${trialDaysLeft} days left` : 'Trial expires today'} • 
                  Track up to {plans[trialInfo.trial_plan]?.item_limit} items
                </div>
              </div>
              <button
                onClick={() => upgradeSubscription('basic')}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-500">Tracked Items</h3>
                <div className="text-2xl font-bold text-gray-900">
                  {items.length} / {currentPlan?.item_limit || 0}
                </div>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                <span>📊</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-500">Current Plan</h3>
                <div className="text-2xl font-bold text-gray-900">{currentPlan?.name}</div>
              </div>
              <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                <span>💎</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-500">Alerts Sent</h3>
                <div className="text-2xl font-bold text-gray-900">0</div>
              </div>
              <div className="w-8 h-8 bg-yellow-100 rounded flex items-center justify-center">
                <span>🔔</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Your Tracked Items</h2>
          <div className="flex space-x-3">
            {!canAddMore && (
              <button
                onClick={() => upgradeSubscription('basic')}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
              >
                Upgrade Plan
              </button>
            )}
            <button
              onClick={() => canAddMore ? setShowAddModal(true) : upgradeSubscription('basic')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {canAddMore ? 'Add Item' : 'Upgrade to Add More'}
            </button>
          </div>
        </div>

        {/* Items Grid */}
        {items.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No items tracked yet</h3>
            <p className="text-gray-600 mb-6">Start by adding your first product to track prices and get alerts.</p>
            <button
              onClick={() => canAddMore ? setShowAddModal(true) : upgradeSubscription('basic')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Your First Item
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow overflow-hidden">
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                    {item.title}
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-lg font-bold text-green-600">
                      {item.current_price ? `$${item.current_price.toFixed(2)}` : 'Price N/A'}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {item.retailer}
                    </div>
                  </div>
                  {item.target_price && (
                    <div className="text-sm text-gray-600 mb-2">
                      Target: ${item.target_price.toFixed(2)}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm">
                      {item.is_stock_alert && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          Stock Alert
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs ${
                        item.is_in_stock 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.is_in_stock ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </div>
                    <button
                      onClick={() => deleteItem(item.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">Add Item to Track</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setScrapedProduct(null);
                  setNewItemUrl('');
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product URL (Amazon, Walmart, or Target)
                </label>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    value={newItemUrl}
                    onChange={(e) => setNewItemUrl(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://www.amazon.com/product..."
                  />
                  <button
                    onClick={scrapeProduct}
                    disabled={loading || !newItemUrl}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Fetching...' : 'Fetch'}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              {scrapedProduct && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Product Preview</h4>
                  <div className="flex space-x-4">
                    {scrapedProduct.image_url && (
                      <img
                        src={scrapedProduct.image_url}
                        alt={scrapedProduct.title}
                        className="w-24 h-24 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 mb-1">{scrapedProduct.title}</h5>
                      <div className="text-lg font-bold text-green-600 mb-2">
                        {scrapedProduct.price ? `$${scrapedProduct.price.toFixed(2)}` : 'Price not available'}
                      </div>
                      <div className="text-sm text-gray-600 capitalize">
                        {scrapedProduct.retailer} • {scrapedProduct.in_stock ? 'In Stock' : 'Out of Stock'}
                      </div>
                      
                      {(!scrapedProduct.price || scrapedProduct.title.includes('blocked') || scrapedProduct.title.includes('not found')) && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                          <p className="text-sm text-yellow-800 mb-2">
                            <strong>Having trouble?</strong> You can still track this product by entering the details manually:
                          </p>
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="Product title"
                              className="w-full px-2 py-1 text-sm border border-yellow-300 rounded"
                              onChange={(e) => setScrapedProduct({
                                ...scrapedProduct,
                                title: e.target.value || scrapedProduct.title
                              })}
                            />
                            <input
                              type="number"
                              step="0.01"
                              placeholder="Current price"
                              className="w-full px-2 py-1 text-sm border border-yellow-300 rounded"
                              onChange={(e) => setScrapedProduct({
                                ...scrapedProduct,
                                price: e.target.value ? parseFloat(e.target.value) : null
                              })}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Target Price (optional)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={targetPrice}
                        onChange={(e) => setTargetPrice(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="25.99"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="stockAlert"
                        checked={isStockAlert}
                        onChange={(e) => setIsStockAlert(e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="stockAlert" className="text-sm text-gray-700">
                        Alert me when back in stock
                      </label>
                    </div>

                    <button
                      onClick={addItem}
                      disabled={loading}
                      className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Adding...' : 'Add to Tracking'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

// Small shell that wraps public deals pages with the shared header/footer
const PublicShell = ({ children }) => {
  const { user, logout } = useAuth();
  return (
    <DealsLayout user={user} onLogout={logout}>
      {children}
    </DealsLayout>
  );
};

const LoginRoute = () => {
  const { user } = useAuth();
  if (user) return <Navigate to="/tracker" replace />;
  return <LandingPage />;
};

const TrackerRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-black text-amber-400">Loading…</div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <Dashboard />;
};

const AdminRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-black text-amber-400">Loading…</div>
  );
  return (
    <PublicShell>
      <AdminDashboard user={user} />
    </PublicShell>
  );
};

const ReferralsRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-black text-amber-400">Loading…</div>
  );
  return (
    <PublicShell>
      <ReferralsPage user={user} />
    </PublicShell>
  );
};

const AppContent = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-xl text-amber-400">Loading DealPing…</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public hot-deals discovery pages */}
      <Route path="/" element={<PublicShell><DealsHome /></PublicShell>} />
      <Route path="/deals" element={<PublicShell><DealsList mode="all" /></PublicShell>} />
      <Route path="/deals/category/:slug" element={<PublicShell><DealsList mode="category" /></PublicShell>} />
      <Route path="/deals/store/:slug" element={<PublicShell><DealsList mode="store" /></PublicShell>} />
      <Route path="/deals/:id" element={<PublicShell><DealDetail /></PublicShell>} />
      <Route path="/subscribe" element={<PublicShell><SubscribePage /></PublicShell>} />

      {/* Auth-gated legacy tracker + admin */}
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/tracker" element={<TrackerRoute />} />
      <Route path="/referrals" element={<ReferralsRoute />} />
      <Route path="/admin" element={<AdminRoute />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;